import { Toast } from '@byecode/ui'
import { ClassNode, type FlowNode, useAtomData } from '@lighthouse/shared'
import { getBrowser } from '@lighthouse/tools'
import type { ReactFlowState } from '@xyflow/react'
import { useReactFlow, useStore } from '@xyflow/react'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

import { copiedFlowNodeSnapshotAtom } from '@/atoms/workSpace/state'
import { useFlow, useFlowContainerContext } from '@/contexts/FlowContext'

import { createFlowNodeClipboardSnapshot, createPastedFlowNode } from '../utils/copyNode'

const isEditableTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
        return false
    }

    if (target.isContentEditable || !!target.closest('[contenteditable="true"]')) {
        return true
    }

    return ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)
}

const getFlowRoot = (root?: HTMLDivElement | null) => {
    return root?.querySelector('.react-flow')
}

export const useCopyPasteFlowNode = () => {
    const { id, log, disable, elRef, setIsChanged } = useFlowContainerContext()
    const readonly = !!log
    const { setNodes } = useFlow()
    const { screenToFlowPosition } = useReactFlow<FlowNode>()
    const setCopiedFlowNodeSnapshot = useSetAtom(copiedFlowNodeSnapshotAtom)
    const copiedFlowNodeSnapshot = useAtomData(copiedFlowNodeSnapshotAtom)
    const isFlowActiveRef = useRef(false)
    const latestMouseClientPositionRef = useRef<{ x: number; y: number } | null>(null)

    const selector = useCallback(
        (store: ReactFlowState) => ({
            selectedNodes: (store.nodes as FlowNode[]).filter(node => node.selected)
        }),
        []
    )

    const { selectedNodes } = useStore(selector)

    const isMac = useMemo(() => getBrowser().getBrowserInfo().platform === 'Macintosh', [])

    useEffect(() => {
        const handleInteraction = (event: Event) => {
            const flowRoot = getFlowRoot(elRef?.current)
            isFlowActiveRef.current = !!(flowRoot && event.target instanceof Node && flowRoot.contains(event.target))

            if (flowRoot && event.target instanceof Node && flowRoot.contains(event.target) && event instanceof MouseEvent) {
                latestMouseClientPositionRef.current = {
                    x: event.clientX,
                    y: event.clientY
                }
            }
        }

        const handlePointerMove = (event: PointerEvent) => {
            const flowRoot = getFlowRoot(elRef?.current)
            if (!(flowRoot && event.target instanceof Node && flowRoot.contains(event.target))) {
                return
            }

            latestMouseClientPositionRef.current = {
                x: event.clientX,
                y: event.clientY
            }
        }

        document.addEventListener('pointerdown', handleInteraction, true)
        document.addEventListener('pointermove', handlePointerMove, true)
        document.addEventListener('focusin', handleInteraction, true)

        return () => {
            document.removeEventListener('pointerdown', handleInteraction, true)
            document.removeEventListener('pointermove', handlePointerMove, true)
            document.removeEventListener('focusin', handleInteraction, true)
        }
    }, [elRef, screenToFlowPosition])

    const handleCopyPaste = useCallback(
        (event: KeyboardEvent, hotkeysEvent: { key: string }) => {
            if (readonly || disable || isEditableTarget(event.target)) {
                return
            }

            const flowRoot = getFlowRoot(elRef?.current)
            const isEventWithinFlow = !!(flowRoot && event.target instanceof Node && flowRoot.contains(event.target))
            if (!isEventWithinFlow && !isFlowActiveRef.current) {
                return
            }

            const copyKey = isMac ? '⌘+c' : 'ctrl+c'
            const pasteKey = isMac ? '⌘+v' : 'ctrl+v'

            if (hotkeysEvent.key === copyKey) {
                if (selectedNodes.length > 1) {
                    event.preventDefault()
                    Toast.info('当前仅支持复制单个节点')
                    return
                }

                const selectedNode = selectedNodes[0]
                if (!selectedNode) {
                    return
                }

                if (selectedNode.type === ClassNode.TRIGGER || selectedNode.type === ClassNode.END) {
                    event.preventDefault()
                    Toast.info('开始节点和结束节点不能被复制')
                    return
                }

                event.preventDefault()
                setCopiedFlowNodeSnapshot({
                    workflowId: id,
                    node: createFlowNodeClipboardSnapshot(selectedNode),
                    pasteCount: 0
                })
                return
            }

            if (hotkeysEvent.key !== pasteKey) {
                return
            }

            const { workflowId, node, pasteCount } = copiedFlowNodeSnapshot
            if (!node) {
                return
            }

            if (workflowId !== id) {
                event.preventDefault()
                Toast.info('暂不支持跨流程粘贴')
                return
            }

            event.preventDefault()
            const pastePosition = latestMouseClientPositionRef.current
                ? screenToFlowPosition(latestMouseClientPositionRef.current)
                : undefined

            const { node: pastedNode, pasteCount: nextPasteCount } = createPastedFlowNode(node, pasteCount, pastePosition)
            setNodes?.(nodes => [...nodes.map(currentNode => ({ ...currentNode, selected: false })), { ...pastedNode, selected: true }])
            setIsChanged?.(true)
            setCopiedFlowNodeSnapshot({
                workflowId,
                node,
                pasteCount: nextPasteCount
            })
        },
        [
            copiedFlowNodeSnapshot,
            disable,
            elRef,
            id,
            isMac,
            readonly,
            screenToFlowPosition,
            selectedNodes,
            setCopiedFlowNodeSnapshot,
            setIsChanged,
            setNodes
        ]
    )

    useHotkeys('ctrl+c, ⌘+c, ctrl+v, ⌘+v', handleCopyPaste, [handleCopyPaste])
}
